import classNames from "classnames";
import LsnCuratedSearchPromptCard from "../../components/autopilotCuratedSearch/CuratedSearchPrompt/LsnCuratedSearchPromptCard";
import CiroBreadCrumbs from "../../components/shared/CiroBreadCrumbs";
import CiroContainer from "../../components/shared/CiroContainer";
import CiroTitle from "../../components/shared/CiroTitle";
// import CuratedLinkedinSalesNavigatorSearchFiltersCard from "../../components/autopilotCuratedSearch/CuratedSearchFilters/CuratedLinkedinSalesNavigatorSearchFiltersCard";
import {
  AutopilotCuratedSearch_AutopilotCuratedSearchQuery,
  AutopilotCuratedSearch_AutopilotCuratedSearchQueryVariables,
  AutopilotCuratedSearch_UpsertAutopilotCuratedSearchMutation,
  AutopilotCuratedSearch_UpsertAutopilotCuratedSearchMutationVariables,
  LinkedinSalesNavigatorFilters,
} from "../../__generated__/graphql";
import { gql, useMutation, useQuery } from "@apollo/client";
import AutopilotCuratedSearchContext from "../../contexts/AutopilotCuratedSearchContext";
import { useContext, useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { hasFiltersApplied } from "../../components/autopilotCuratedSearch/curatedSearchUtils";
import sendPayloadToExtension from "../../utils/sendPayloadToExtension";
import { LAUNCH_AUTOPILOT } from "../../utils/sendPayloadToExtension";
import CuratedSearchInstallExtension from "../../components/autopilotCuratedSearch/CuratedSearchInstallExtension";
import AppContext from "../../contexts/AppContext";
import * as Sentry from "@sentry/react";
import AutopilotCuratedLinkedinSalesNavigatorSearchContext from "../../contexts/AutopilotCuratedLinkedinSalesNavigatorSearchContext";

const AutopilotCuratedSearch_UpsertAutopilotCuratedSearch = gql`
  mutation AutopilotCuratedSearch_UpsertAutopilotCuratedSearch(
    $input: UpsertAutopilotCuratedSearchInput!
  ) {
    upsertAutopilotCuratedSearch(input: $input) {
      success
      autopilotCuratedSearch {
        id
        linkedinSalesNavigatorCuratedSearch {
          url
        }
        prompt
      }
    }
  }
`;

const AutopilotCuratedSearch_AutopilotCuratedSearch = gql`
  query AutopilotCuratedSearch_AutopilotCuratedSearch(
    $id: Int
    $skip: Boolean!
  ) {
    personas {
      id
      name
      description
      source
    }
    autopilotCuratedSearches {
      id
      prompt
    }
    autopilotCuratedSearch(id: $id) @skip(if: $skip) {
      id
      prompt
      linkedinSalesNavigatorCuratedSearch {
        filters {
          jobTitles {
            label
          }
          industries {
            id
            label
          }
          geographies {
            id
            label
          }
          companyHQLocations {
            id
            label
          }
          companyHeadCounts {
            id
            label
          }
          companyNames {
            label
          }
          excludedCompanyNames {
            label
          }
          keywords
        }
      }
    }
  }
`;

const AutopilotCuratedSearch = ({
  onboarding = false,
}: {
  onboarding?: boolean;
}) => {
  const { curatedSearchId } = useParams();

  const { user } = useContext(AppContext);

  const {
    data: autopilotCuratedSearchData,
    loading: autopilotCuratedSearchLoading,
  } = useQuery<
    AutopilotCuratedSearch_AutopilotCuratedSearchQuery,
    AutopilotCuratedSearch_AutopilotCuratedSearchQueryVariables
  >(AutopilotCuratedSearch_AutopilotCuratedSearch, {
    variables: {
      id: curatedSearchId ? parseInt(curatedSearchId) : undefined,
      skip: !curatedSearchId,
    },
    fetchPolicy: "network-only",
  });

  const { isExtensionInstalled } = useContext(AppContext);

  const [
    upsertAutopilotCuratedSearch,
    { loading: upsertAutopilotCuratedSearchLoading },
  ] = useMutation<
    AutopilotCuratedSearch_UpsertAutopilotCuratedSearchMutation,
    AutopilotCuratedSearch_UpsertAutopilotCuratedSearchMutationVariables
  >(AutopilotCuratedSearch_UpsertAutopilotCuratedSearch);

  const [selectedPrompt, setSelectedPrompt] = useState<string>("");
  const [startedGeneratingFilters, setStartedGeneratingFilters] =
    useState(false);

  const onSaveFilters = async ({
    currentFilters,
    additionalFiltering,
  }: {
    currentFilters: LinkedinSalesNavigatorFilters | null;
    additionalFiltering: string[];
  }) => {
    if (!selectedPrompt) {
      toast.error("Search not found");
      return;
    }
    if (!currentFilters || !hasFiltersApplied({ filters: currentFilters })) {
      toast.error("No filters applied");
      return;
    }
    toast.success("Launching Sales Navigator...");
    await upsertAutopilotCuratedSearch({
      variables: {
        input: {
          id: curatedSearchId ? parseInt(curatedSearchId) : undefined,
          promptText: selectedPrompt,
          scoringCriteria: additionalFiltering.map((c) => ({
            criterion: c,
          })),
          linkedinSalesNavigatorFilters: {
            keywords: currentFilters?.keywords,
            jobTitles: currentFilters?.jobTitles?.map((j) => ({
              label: j.label,
            })),
            industries: currentFilters?.industries?.map((i) => ({
              id: i.id,
              label: i.label,
            })),
            geographies: currentFilters?.geographies?.map((g) => ({
              id: g.id,
              label: g.label,
            })),
            companyHQLocations: currentFilters?.companyHQLocations?.map(
              (c) => ({
                id: c.id,
                label: c.label,
              }),
            ),
            companyHeadCounts: currentFilters?.companyHeadCounts?.map((c) => ({
              id: c.id,
              label: c.label,
            })),
            companyNames: currentFilters?.companyNames?.map((c) => ({
              label: c.label,
            })),
            excludedCompanyNames: currentFilters?.excludedCompanyNames?.map(
              (c) => ({
                label: c.label,
              }),
            ),
          },
        },
      },
      onCompleted: async (data) => {
        const redirectUrl =
          data.upsertAutopilotCuratedSearch.autopilotCuratedSearch
            ?.linkedinSalesNavigatorCuratedSearch?.url;
        if (redirectUrl) {
          try {
            await sendPayloadToExtension({
              payload: {
                action: LAUNCH_AUTOPILOT,
                url: redirectUrl,
                curatedSearchId:
                  data?.upsertAutopilotCuratedSearch?.autopilotCuratedSearch
                    ?.id,
              },
            });
          } catch (error) {
            Sentry.captureException(
              "Error launching Sales Navigator with payload for user " +
                user?.email,
              {
                extra: {
                  error,
                  redirectUrl,
                },
              },
            );
            if (isExtensionInstalled) {
              // This case sometimes happens when the extension is installed but for some reason
              // the specific LAUNCH_AUTOPILOT message is not being received or there's an error in handling it.
              window.open(redirectUrl, "_blank");
            } else {
              toast.error(
                "Error launching Sales Navigator. Please install the Ciro extension first.",
              );
            }
          }
        }
      },
    });
  };

  return (
    <AutopilotCuratedSearchContext.Provider
      value={{
        selectedPrompt,
        setSelectedPrompt: (prompt) => {
          setSelectedPrompt(prompt || "");
        },
        startedGeneratingFilters,
        setStartedGeneratingFilters,
        upsertAutopilotCuratedSearchLoading,
        historicalPrompts: (autopilotCuratedSearchData?.autopilotCuratedSearches
          ?.map((c) => c.prompt)
          .filter(Boolean) ?? []) as string[],
        personas: autopilotCuratedSearchData?.personas ?? [],
        reset: () => {
          setStartedGeneratingFilters(false);
        },
      }}
    >
      <AutopilotCuratedLinkedinSalesNavigatorSearchContext.Provider
        value={{
          onSaveFilters,
        }}
      >
        <CiroContainer className={classNames("bg-zinc-100")}>
          <CiroBreadCrumbs
            values={[
              "Autopilot",
              onboarding
                ? "Start searching"
                : "Search LinkedIn Sales Navigator",
            ]}
            href="/autopilot"
          />
          <div className={classNames("pb-4")}>
            <CiroTitle
              title={
                onboarding
                  ? "Start searching"
                  : "Search LinkedIn Sales Navigator"
              }
              subtitle="Describe your desired search results"
            />
          </div>
          {!isExtensionInstalled && <CuratedSearchInstallExtension />}

          <div className={classNames("flex", "flex-col", "gap-4", "w-full")}>
            <div
              className={classNames("w-full", {
                "opacity-50": upsertAutopilotCuratedSearchLoading,
                "pointer-events-none": upsertAutopilotCuratedSearchLoading,
              })}
            >
              <LsnCuratedSearchPromptCard
                initialLoading={autopilotCuratedSearchLoading}
              />
            </div>
            {/* <div className={classNames("w-full")}>
              <CiroTooltipContainer
                tooltip={filtersCardHiddenMessage}
                disabled={!filtersCardHiddenMessage}
              >
                <div
                  className={classNames({
                    "pointer-events-none": Boolean(filtersCardHiddenMessage),
                  })}
                >
                  <CuratedLinkedinSalesNavigatorSearchFiltersCard
                    currentFilters={currentFilters}
                    setCurrentFilters={setCurrentFilters}
                    initialLoading={autopilotCuratedSearchLoading}
                    additionalFiltering={additionalFiltering}
                    setAdditionalFiltering={setAdditionalFiltering}
                    onSave={async () => {
                      await onSaveFilters();
                    }}
                  />
                </div>
              </CiroTooltipContainer>
            </div> */}
          </div>
        </CiroContainer>
      </AutopilotCuratedLinkedinSalesNavigatorSearchContext.Provider>
    </AutopilotCuratedSearchContext.Provider>
  );
};

export default AutopilotCuratedSearch;
